import React, { type FC, memo, useCallback, useEffect, useId, useRef, useState } from "react";
import { Button } from "@heroui/react";

import { AgentStateIcon } from "./AgentStateIcon";
import type { AgentMessage, MessageValues } from "./runner/types";
import { type MessageId, useTranslation } from "@domain/intl/useTranslation";

type Approval = NonNullable<AgentMessage["approval"]>;

interface Props {
    textId: MessageId;
    values?: MessageValues;
    approval: Approval;
    /** Answers the pending question behind the card; absent when nothing is pending. */
    onAnswer?(value: "go" | "skip"): void;
}

const answerLabels: Record<NonNullable<Approval["answer"]>, MessageId> = {
    go: "app.agent.approve.granted",
    skip: "app.agent.approve.skipped",
    auto: "app.agent.approve.auto",
};

const AgentApprovalCardImpl: FC<Props> = ({ textId, values, approval, onAnswer }) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const goRef = useRef<HTMLButtonElement>(null);
    const detailsId = useId();
    const { answer } = approval;

    useEffect(() => {
        if (!answer) goRef.current?.focus();
    }, [answer]);

    const handleGo = useCallback(() => onAnswer?.("go"), [onAnswer]);
    const handleSkip = useCallback(() => onAnswer?.("skip"), [onAnswer]);
    const handleToggle = useCallback(() => setExpanded((e) => !e), []);

    const question = t(textId, values);

    if (answer) {
        return (
            <div
                data-testid="agent-approval"
                className="flex max-w-full min-w-0 items-center gap-1.5 self-start text-sm text-muted"
            >
                <AgentStateIcon state={answer === "skip" ? "skipped" : "pending"} className="size-4 shrink-0" />
                <strong className="shrink-0">{t(answerLabels[answer])}</strong>
                <span className="min-w-0 truncate" title={question}>
                    {question}
                </span>
            </div>
        );
    }

    return (
        <div
            data-testid="agent-approval"
            role="group"
            aria-label={question}
            className="max-w-[85%] self-start rounded-xl rounded-bl-sm border border-accent bg-surface px-3 py-2 text-foreground"
        >
            <div className="flex gap-1.5">
                <AgentStateIcon state="asking" className="mt-0.5 size-4" />
                <div className="w-full">
                    <div className="font-semibold">{question}</div>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        <Button ref={goRef} variant="primary" className="rounded-full" onPress={handleGo}>
                            {t(approval.goLabelId)}
                        </Button>
                        <Button variant="secondary" className="rounded-full" onPress={handleSkip}>
                            {t("app.agent.approve.skip")}
                        </Button>
                        <button
                            type="button"
                            className="ml-auto text-sm text-accent underline"
                            aria-expanded={expanded}
                            aria-controls={detailsId}
                            onClick={handleToggle}
                        >
                            {t("app.agent.approve.learnMore")}
                        </button>
                    </div>
                    <div
                        id={detailsId}
                        hidden={!expanded}
                        className="mt-3 whitespace-pre-line rounded-lg bg-default p-3 text-sm"
                    >
                        {t(approval.learnMoreId)}
                    </div>
                </div>
            </div>
        </div>
    );
};

export const AgentApprovalCard = memo(AgentApprovalCardImpl);
